import React, { useMemo, useCallback } from 'react';
import { WordCloud } from "@isoterik/react-word-cloud";
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { utils, writeFile } from 'xlsx';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const WordCloudDisplay = ({ conversations }) => {
  const wordData = useMemo(() => {
    if (conversations.length === 0) return [];

    const stopWords = new Set([
      'de', 'la', 'que', 'el', 'en', 'y', 'a', 'los', 'del', 'se', 'no',
      'con', 'para', 'mi', 'es', 'por', 'un', 'una', 'las', 'me', 'si', 
      'lo', 'su', 'pero', 'mas', 'al', 'como', 'le', 'ya', 'o', 'este', 
      'muy', 'sin', 'cuando', 'sobre', 'también', 'hasta', 'hay', 'donde', 
      'quien', 'desde', 'todo', 'nos', 'durante', 'uno', 'otro', 'esa', 
      'eso', 'hola', 'gracias', 'buenos', 'dias', 'tardes', 'noches', 'ok',
      'está', 'son', 'será', 'han', 'fue', 'ser', 'hacer', 'puede', 'bien',
      'más', 'sí', 'te', 'yo', 'él', 'ella', 'nosotros', 'ellos', 'ellas',
      'soy', 'tengo', 'años'
    ]);

    const wordCounts = new Map();

    conversations.forEach(conv => {
      if (!conv.messages) return;

      conv.messages.forEach(message => {
        if (message.role !== 'user' || typeof message.content !== 'string') return;

        const cleanText = message.content
          .toLowerCase()
          .replace(/[^\w\sáéíóúñü]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();

        const words = cleanText.split(' ');

        words.forEach(word => {
          const isNumeric = !isNaN(word);
          const isLikelyAge = isNumeric && Number(word) >= 18 && Number(word) <= 99;

          if (word.length > 1 && !stopWords.has(word) && (!isNumeric || isLikelyAge)) {
            wordCounts.set(word, (wordCounts.get(word) || 0) + 1);
          }
        });
      });
    });

    return Array.from(wordCounts.entries())
      .map(([text, value]) => ({ text, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 30);
  }, [conversations]);

  const customFontSize = useCallback((word) => {
    if (wordData.length === 0) return 40;
    
    const maxValue = Math.max(...wordData.map(w => w.value));
    const minValue = Math.min(...wordData.map(w => w.value));
    const maxSize = 100;
    const minSize = 30;
    
    if (maxValue === minValue) return maxSize;
    
    const normalized = (word.value - minValue) / (maxValue - minValue);
    const size = minSize + (maxSize - minSize) * Math.sqrt(normalized);
    
    return Math.max(minSize, Math.min(maxSize, size));
  }, [wordData]);

  const customColor = useCallback((word, index) => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
      '#DDA0DD', '#98D8C8', '#6C5CE7', '#A29BFE', '#FD79A8',
      '#E17055', '#00B894', '#00CEC9', '#6C5CE7', '#FDCB6E'
    ];
    return colors[index % colors.length];
  }, []);

  const barChartData = useMemo(() => {
    const topWords = wordData.slice(0, 15);
    return {
      labels: topWords.map(word => word.text),
      datasets: [{
        label: 'Frecuencia de palabras',
        data: topWords.map(word => word.value),
        backgroundColor: [
          'rgba(255, 99, 132, 0.8)','rgba(54, 162, 235, 0.8)','rgba(255, 205, 86, 0.8)','rgba(75, 192, 192, 0.8)','rgba(153, 102, 255, 0.8)','rgba(255, 159, 64, 0.8)','rgba(199, 199, 199, 0.8)','rgba(83, 102, 255, 0.8)','rgba(255, 99, 255, 0.8)','rgba(99, 255, 132, 0.8)','rgba(255, 132, 99, 0.8)','rgba(132, 255, 99, 0.8)','rgba(99, 132, 255, 0.8)','rgba(255, 255, 99, 0.8)','rgba(99, 255, 255, 0.8)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)','rgba(54, 162, 235, 1)','rgba(255, 205, 86, 1)','rgba(75, 192, 192, 1)','rgba(153, 102, 255, 1)','rgba(255, 159, 64, 1)','rgba(199, 199, 199, 1)','rgba(83, 102, 255, 1)','rgba(255, 99, 255, 1)','rgba(99, 255, 132, 1)','rgba(255, 132, 99, 1)','rgba(132, 255, 99, 1)','rgba(99, 132, 255, 1)','rgba(255, 255, 99, 1)','rgba(99, 255, 255, 1)'
        ],
        borderWidth: 2
      }]
    };
  }, [wordData]);

  const barChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { font: { size: 14 } } },
      title: { display: true, text: 'Top 15 Palabras Más Frecuentes (Análisis Estadístico)', font: { size: 16, weight: 'bold' } },
      tooltip: { callbacks: { label: (context) => `${context.label}: ${context.parsed.y} ${context.parsed.y === 1 ? 'vez' : 'veces'}` } }
    },
    scales: {
      y: { beginAtZero: true, title: { display: true, text: 'Frecuencia', font: { size: 14, weight: 'bold' } }, ticks: { font: { size: 12 } } },
      x: { title: { display: true, text: 'Palabras', font: { size: 14, weight: 'bold' } }, ticks: { maxRotation: 45, minRotation: 45, font: { size: 12 } } }
    }
  }), []);

  const downloadStatisticalAnalysis = useCallback(() => {
    const ws = utils.json_to_sheet(wordData.map(word => ({ Palabra: word.text, Frecuencia: word.value })));
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Análisis Estadístico');
    const downloadDate = new Date().toISOString().slice(0, 10);
    writeFile(wb, `Analisis_Estadistico_descargado_${downloadDate}.xlsx`);
  }, [wordData]);

  if (wordData.length === 0) {
    return null;
  }

  return (
    <>
      <details className="mb-8 group">
        <summary className="text-xl font-bold text-gray-800 cursor-pointer list-none flex items-center">
          🌤️ Nube de Palabras
          <span className="ml-2 text-gray-500 transform transition-transform group-open:rotate-90">&#9654;</span>
        </summary>
        <div className="mt-4">
          <div className="border border-gray-300 rounded-lg bg-white p-4">
            <div className="w-full flex justify-center">
              <div style={{ width: "1100px", height: "600px", backgroundColor: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <WordCloud 
                  words={wordData} 
                  width={1100}
                  height={600}
                  fontSize={customFontSize}
                  fill={customColor}
                  rotate={() => 0}
                  spiral="archimedean"
                  padding={5}
                  random={() => 0.5}
                />
              </div>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-600 text-center">
            Mostrando las {Math.min(30, wordData.length)} palabras más frecuentes de {wordData.length} total
            {wordData.length > 0 && (
              <span className="ml-2">
                (Rango: {wordData[wordData.length - 1]?.value} - {wordData[0]?.value} menciones)
              </span>
            )}
          </div>
        </div>
      </details>

      <details className="mb-6 group">
        <summary className="text-xl font-bold text-gray-800 cursor-pointer list-none flex items-center">
          📊 Análisis Estadístico
          <span className="ml-2 text-gray-500 transform transition-transform group-open:rotate-90">&#9654;</span>
        </summary>
        <div className="flex justify-end mt-4">
          <button onClick={downloadStatisticalAnalysis} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm">
            📥 Descargar Excel
          </button>
        </div>
        <div className="mt-4">
          <div className="bg-white border border-gray-300 rounded-lg p-4">
            <div style={{ height: '400px' }}>
              <Bar data={barChartData} options={barChartOptions} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-semibold text-blue-800 mb-2">Total de Palabras Únicas</h5>
                <p className="text-2xl font-bold text-blue-600">{wordData.length}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h5 className="font-semibold text-green-800 mb-2">Palabra Más Frecuente</h5>
                <p className="text-lg font-bold text-green-600">
                  {wordData[0]?.text} ({wordData[0]?.value} veces)
                </p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-semibold text-purple-800 mb-2">Total de Menciones (Top 30)</h5>
                <p className="text-2xl font-bold text-purple-600">
                  {wordData.reduce((acc, word) => acc + word.value, 0)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </details>
    </>
  );
};

export default WordCloudDisplay;
